import React, { useEffect, useState, ChangeEvent, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  Button,
  Row,
  Col,
  Alert,
} from 'reactstrap';
import { useUser } from 'context/UserContext';
import { useAlertPlus, useConfirm } from 'hooks';
import Required from 'components/Required';
import ConfirmModal from 'components/ConfirmModal';
import AlertModal from 'components/AlertModal';
import UploaderMultiple from 'components/UploaderMultiple';
import HonorariosEntidades from 'components/MatriculateRequest/HonorariosEntidades';
import {
  getAllConsultoriosCokiba,
  getAllConsultoriosAsDirector,
} from '../../features/consultorios/consultoriosAPI';
import {
  getStaffRequest,
  putConsultorioStaff,
} from '../../features/staff/staffAPI';
import { getMatriculateInfo } from '../../features/matriculates/matriculatesAPI';
import {
  ConsultorioCokiba,
  ConsultorioCokibaBD,
  HorariosAtencionRegistrado,
} from 'types/Consultorio';
import { horariosRegistradosFromConsultorios, validarSolapamientos } from '../../utils/horariosValidacion';
import {
  StaffConsultorioRequest,
} from 'types/Staff';
import HorariosAtencionComponent from 'components/Consultorios/HorariosAtencion';

interface MatriculateInfo {
  matricula: number;
  nombre: string;
}

export default function EditSolicitudStaff() {
  const { id } = useParams<any>();
  const { getMatricula } = useUser();
  const matriculateId = getMatricula();
  if (!matriculateId) {
    return null;
  }

  const history = useHistory();

  const [formValidation, setFormValidation] = useState<
    Partial<Record<keyof StaffConsultorioRequest, boolean>>
  >({});

  const [disableSend, setDisableSend] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const [showAlert, message, alertCallback, alert] = useAlertPlus('');
  const [confirm, confirmTitle, confirmAction, setConfirm] = useConfirm(false);

  const formElement = useRef<HTMLFormElement | null>(null);

  const [consultorios, setConsultorios] = useState<ConsultorioCokibaBD[]>([]);
  const [search, setSearch] = useState('');

  const [directorTecnico, setDirectorTecnico] = useState<MatriculateInfo>({
    matricula: 0,
    nombre: '',
  });

  const [canUpdateByState, setCanUpdateByState] = useState(false);
  const [canUpdateAttachs, setCanUpdateAttachs] = useState(false);

  const [data, setData] = useState<StaffConsultorioRequest>({
    id: 0,
    state_id: null,
    matriculate_id: null,
    director_tecnico: null,
    consultorio_id: null,
    es_director: false,
    created_at: null,
    resolved_at: null,
    deleted_at: null,
    user_id: null,
    state: null,
    motivo_rechazo: null,
    attachs: [],
    horarios_atencion: [],
  });

  const [horariosAtencion, setHorariosAtencion] = useState<HorariosAtencionRegistrado[]>([]);

  useEffect(() => {
    setLoading(true);

    const loadHorarios = async () => {
      const consultorios: ConsultorioCokiba[] = await getAllConsultoriosCokiba(matriculateId) as ConsultorioCokiba[];
      if (consultorios) {
        const ordenDias = [
          'LUNES',
          'MARTES',
          'MIERCOLES',
          'JUEVES',
          'VIERNES',
          'SABADO',
          'DOMINGO',
        ];

        const horarios = horariosRegistradosFromConsultorios(consultorios)
          .sort((a, b) => {
            const diffDias = ordenDias.indexOf(a.diaSemana) - ordenDias.indexOf(b.diaSemana);
            if (diffDias !== 0) return diffDias;
            return a.horaInicio.localeCompare(b.horaInicio);
          });

        setHorariosAtencion(horarios);
      }
    };

    const loadData = async () => {
      try {
        const staffRequest: StaffConsultorioRequest = await getStaffRequest(id) as StaffConsultorioRequest;
        if (!!staffRequest) {
          setData({
            ...staffRequest,
          });
        } else {
          throw new Error('No se pudo recuperar la solicitud de consultorio');
        }
      } catch (err) {
        setDisableSend(true);
        alert(
          'Se produjo un error al intentar recuperar información inicial. ' +
            'Por favor, intente nuevamente más tarde.',
        );
        console.error('ConsultoriosCreate', 'Error al cargar datos iniciales');
        console.info(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    loadHorarios();
  }, [id]);

  useEffect(() => {
    if (!!data.id) {
      const director = `${data.director_tecnico}`;
      setSearch(director);
      onSearch(director);
    }
  }, [data.id]);

  useEffect(() => {
    if (data.state_id == 3) {
      setCanUpdateByState(true);
      setCanUpdateAttachs(true);
    }
  }, [data.state_id]);

  const validarHorarios = (): boolean => {
    if (data.horarios_atencion.length === 0) {
      alert(`Se debe cargar al menos un día y horarios de atención.`);
      return false;
    }

    const resultado = validarSolapamientos(data.horarios_atencion, horariosAtencion);
    if (!resultado.ok) {
      alert(resultado.message ?? 'Hay horarios superpuestos.');
      return false;
    }

    return true;
  };

  const handleFormValidation = () => {
    let newFormValidation = {};

    let toValidate = [
      ...Array.from(document.getElementsByClassName('form-control')),
    ] as HTMLInputElement[];
    toValidate = toValidate.filter(e => !e.disabled && e.required);

    let missing = false;
    // Validar campos required
    toValidate.forEach(field => {
      if (field.required && !field.disabled && !field.value?.trim()) {
        field.classList.add('is-invalid');

        missing = true;
      } else if (field.inputMode === 'numeric') {
        if (!/^\d+$/.test(field.value)) {
          newFormValidation = {
            ...newFormValidation,
            [field.name]: false,
          };
        }
      } else {
        field.classList.remove('is-invalid');
      }
    });

    if (missing) {
      setFormValidation(newFormValidation);
      alert(
        'Todos los campos marcados con * son obligatorios. ' +
          'Por favor complete los faltantes antes de enviar la solicitud.',
      );
      return false;
    }

    // Si los campos estan bien, se revisan los horarios
    return validarHorarios();
  };

  // Confirmación de envío de Solicitud
  const handleSendForm = () => {
    if (!handleFormValidation()) {
      return;
    }

    setConfirm(
      true,
      '¿Estás seguro que deseas enviar esta solicitud de consultorio?',
      'send',
    );
  };

  // Gestor de respuestas de confirmaciones
  const handleConfirmation = (action: string) => {
    if (action === 'send') {
      handleSend();
    }

    setConfirm(false);
  };

  // Envío de solicitudes
  const handleSend = async () => {
    setFormValidation({});

    setLoading(true);

    const directorTecnico = data.director_tecnico;

    // Preparamos los inputs menos los archivos
    const consultorioStaff: Partial<StaffConsultorioRequest> = {};

    if (!formElement.current) return;

    new FormData(formElement.current).forEach((value, key) => {
      // @ts-ignore
      consultorioStaff[key as keyof StaffConsultorioRequest] = value.toString() as any;
    });

    consultorioStaff.director_tecnico = directorTecnico;

    // Seteamos el id
    consultorioStaff['id'] = data.id;

    // Seteamos los horarios
    consultorioStaff['horarios_atencion'] = data.horarios_atencion;

    // Preparamos los archivos
    const files: Record<string, File> = {};
    const inputs = Array.from(document.querySelectorAll('input[type="file"]'));
    for (const inputElement of inputs) {
      const input = inputElement as HTMLInputElement;
      if (input.hasAttribute('required') && input.value === '') {
        alert(
          'Faltan subir archivos requeridos para poder enviar la solicitud.',
        );
        setLoading(false);
        return;
      }

      if (input.files) {
        for (let i = 0; i < input.files.length; i++) {
          files[`${input.name}_${i}`] = input.files[i];
        }
      }
    }

    // Enviamos la petición
    await putConsultorioStaff(consultorioStaff, files)
      .then(res => {
        if (!res || res.status != 'ok') {
          // Error interno
          console.error('ConsultoriosStaffEdit', 'Error al enviar la solicitud');
          console.info(res);
          alert(
            res.message ??
            'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
          );
          return;
        }

        alert('La solicitud se envió correctamente', () => {
          history.push('/consultorios');
        });
      })
      .catch(err => {
        console.error(
          'ConsultoriosStaffEdit',
          'Error inesperado al intentar enviar la solicitud',
        );
        console.info(err);
        alert(
          'Ha ocurrido un error al enviar la solicitud. Por favor intente nuevamente.',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const { target } = event;

    const name = target.name;

    let value: string | number | boolean | null;
    value = target.value;

    if (target.type === 'checkbox') {
      value = target.checked;
    }

    if (
      typeof value === 'string' &&
      ['consultorio_id'].includes(name)
    ) {
      value = !!parseInt(value) ? parseInt(value) : '';
    }

    // Guardamos el nuevo valor
    setData({
      ...data,
      [name]: value,
    });
  };

  const onSearch = async (query: string) => {
    if (query) {
      setLoading(true);

      const loadData = async (matricula: string) => {
        try {
          getAllConsultoriosAsDirector(matricula)
            .then(async response => {
              setConsultorios(response);

              setDirectorTecnico({
                matricula: 0,
                nombre: '',
              });
              setData({
                ...data,
                director_tecnico: null,
              });

              const matriculate: MatriculateInfo = await getMatriculateInfo(+query) as MatriculateInfo;
              if (!!matriculate) {
                setDirectorTecnico(matriculate);
                setData({
                  ...data,
                  director_tecnico: matriculate.matricula,
                });
              }
            })
            .catch(err => {
              alert(true, err.getMessage() || 'No se puedieron obtener los pedidos del matriculado');
            })
            .finally(() => {
              setLoading(false);
            });
        } catch (err) {
          setDisableSend(true);
          alert(
            'Se produjo un error al intentar recuperar información inicial. ' +
              'Por favor, intente nuevamente más tarde.',
          );
          console.error('ConsultoriosCreate', 'Error al cargar datos iniciales');
          console.info(err);
        } finally {
          setLoading(false);
        }
      };

      await loadData(query);
    }
  };

  return (
    <div className="content">
      { data.motivo_rechazo
        ?
          <Alert color="warning">
            <Row className="mb-2">
              <Col sm={12}>
                <span className='group-name observations'>
                  Motivo de rechazo
                  <p className="mt-2">
                    { data.motivo_rechazo }
                  </p>
                </span>
              </Col>
            </Row>
          </Alert>
        : null
      }
      <Row>
        <Col sm={12}>
          <Card>
            <CardHeader>
              <Row style={{ alignItems: 'center' }}>
                <Col sm={8}>
                  <CardTitle tag="h5">Editar Staff Consultorio</CardTitle>
                </Col>
                <Col sm={4} className="text-right">
                  <Button
                    className="btn-round"
                    color="primary"
                    onClick={() => history.push('/consultorios')}
                  >
                    Volver
                  </Button>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Form innerRef={formElement}>
                <Col sm={12}>
                  <CardTitle>Consultorio</CardTitle>
                </Col>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      <Label for="search">
                        Buscar Matriculado Staff <Required />
                      </Label>
                      <InputGroup>
                        <Input
                          id="search"
                          name="search"
                          type="text"
                          placeholder="Buscar por Matrícula..."
                          onChange={ event => setSearch(event.target.value) }
                          onKeyUp={ event => event.key === 'Enter' && onSearch(search) }
                          value={ search }
                          disabled={isLoading || !canUpdateByState}
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          style={{
                            border: `1px solid '#dddddd'`,
                            borderBottomRightRadius: '.25rem',
                            borderTopRightRadius: '.25rem',
                            width: '40px',
                          }}
                          onClick={ () => onSearch(search) }
                          disabled={isLoading || !canUpdateByState}
                        >
                          <i className="fas fa-search"></i>
                        </button>
                      </InputGroup>
                    </Col>
                    <Col md="6">
                      <Label for="consultorio_id">
                        Consultorio <Required />
                      </Label>
                      <Input
                        id="consultorio_id"
                        name="consultorio_id"
                        type="select"
                        className="custom-select form-control"
                        value={data.consultorio_id ?? ''}
                        invalid={formValidation.consultorio_id === false}
                        onChange={handleInputChange}
                        disabled={isLoading || !canUpdateByState}
                        required
                      >
                        <option value="">Seleccione una opción...</option>
                        {consultorios?.map(item => {
                          return (
                            <option
                              key={item.id}
                              value={item.id}
                            >
                              {item.nombre}, {item.calle} {item.numero}, {item.localidad?.MTR008_D_LOCALIDAD}
                            </option>
                          );
                        })}
                      </Input>
                    </Col>
                    <Col md="6"></Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row>
                    <Col md="6">
                      { data.director_tecnico
                        ?
                          <div className="border rounded p-2 bg-light">
                            <div className="ml-2 mb-2">
                              <strong>Matrícula:</strong> {directorTecnico.matricula}
                            </div>
                            <div className="ml-2 mb-2">
                              <strong>Nombre:</strong> {directorTecnico.nombre}
                            </div>
                          </div>
                        : null
                      }
                    </Col>
                    <Col md="6">
                      <Input
                        id="es_director"
                        name="es_director"
                        type="checkbox"
                        checked={!!data.es_director}
                        invalid={formValidation.es_director === false}
                        onChange={handleInputChange}
                        disabled={isLoading || !canUpdateByState}
                      />
                      <Label
                        for="es_director"
                        style={{
                          marginBottom: 0,
                          marginLeft: '5px',
                          verticalAlign: 'bottom',
                        }}
                      >
                        Soy Director
                      </Label>
                    </Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Atención</CardTitle>
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="documentacion">
                        Días y horarios de atención <Required />
                      </Label>
                      <HorariosAtencionComponent
                        id={data.id}
                        value={data.horarios_atencion}
                        actuales={horariosAtencion}
                        onChange={horarios =>
                          setData({ ...data, horarios_atencion: horarios })
                        }
                        disabled={isLoading || !canUpdateByState}
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <hr className="my-4" />
                <Col sm={12}>
                  <CardTitle>Adjuntos</CardTitle>
                </Col>
                <Col sm={12}>
                  <HonorariosEntidades
                    titulosPagos={[
                      'ARBA_TIMBRADO_EN_BLANCO',
                    ]}
                  />
                </Col>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="constancia_pagos">
                        Constancia de pago <Required />
                      </Label><br/>
                      <UploaderMultiple
                        attachs={data.attachs}
                        name="constancia_pagos"
                        canUpdateAttachs={canUpdateAttachs}
                        required
                      />
                    </Col>
                    <Col md="6">
                      <Label for="solicitud_habilitacion">
                        Nota Tipo 6 solicitando la habilitación (con firma del director técnico) <Required />
                      </Label><br/>
                      <UploaderMultiple
                        attachs={data.attachs}
                        name="solicitud_habilitacion"
                        canUpdateAttachs={canUpdateAttachs}
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="titulos_grado">
                        Título de grado del profesional de la kinesiología <Required />
                      </Label><br/>
                      <UploaderMultiple
                        attachs={data.attachs}
                        name="titulos_grado"
                        canUpdateAttachs={canUpdateAttachs}
                        required
                        multiple
                      />
                    </Col>
                    <Col md="6">
                      <Label for="constancia_cuit">
                        Constancia de CUIT del profesional de la kinesiología <Required />
                      </Label><br/>
                      <UploaderMultiple
                        attachs={data.attachs}
                        name="constancia_cuit"
                        canUpdateAttachs={canUpdateAttachs}
                        required
                        accept={['application/pdf']}
                        multiple
                      />
                    </Col>
                  </Row>
                </FormGroup>
                <FormGroup>
                  <Row className="mt-4 mb-2">
                    <Col md="6">
                      <Label for="resolucion_ministerio">
                        Resolución del Ministerio <Required />
                      </Label><br/>
                      <UploaderMultiple
                        attachs={data.attachs}
                        name="resolucion_ministerio"
                        canUpdateAttachs={canUpdateAttachs}
                        required
                      />
                    </Col>
                  </Row>
                </FormGroup>
                { [3, 6].includes(data.state_id || 0)
                  ?
                    <FormGroup>
                      <div className="text-center">
                        <Button
                          type="button"
                          className="btn-round"
                          onClick={handleSendForm}
                          disabled={isLoading || disableSend}
                        >
                          Enviar
                        </Button>
                      </div>
                    </FormGroup>
                  : null
                }
              </Form>
              <AlertModal
                isOpen={showAlert}
                message={message}
                onClose={alertCallback}
              />
              <ConfirmModal
                isOpen={confirm}
                title={confirmTitle}
                action={confirmAction}
                onClose={() => setConfirm(false)}
                onConfirm={handleConfirmation}
              />
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
